import { withErrorBoundary } from '@sentry/react';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Redirect, useHistory, useRouteMatch } from 'react-router-dom';
import { useToasts } from 'react-toast-notifications';
import { useMutation, useQuery, useSubscription } from 'urql';
import { assets } from '../../assets';
import Button from '../../components/buttons/button';
import { BlurredContainerM2030 } from '../../components/containers/containers';
import DefaultScreen from '../../components/containers/default-screen';
import DataRow from '../../components/data-viewers/data-row/data-row';
import StyledInput from '../../components/inputs/styled-input';
import CenteredSpinner from '../../components/loading-indicators/centered-spinner';
import ErrorShower from '../../components/ui-utils/error-shower';
import { Fallback } from '../../components/utils/error-boundary';
import { formatDate } from '../../helpers/date-formatters';
import { formatNumber } from '../../helpers/number-formatters';
import {
  StatusIcon,
  stringifyExchangeRateType,
  stringifyStatus,
} from '../../helpers/type-formatters';
import { useUserPhoneNumber } from '../../logic/graphql/hooks';
import { GraphqlOperations } from '../../logic/graphql/operations';
import {
  ExchangeOrder,
  ExchangeOrderListItem,
  Query,
  Status,
  Subscription,
} from '../../logic/graphql/types/types.d';
import ExchangeOrders from '../../logic/redux/exchange-orders';
import { EditedCreateExchangeOrder } from '../../logic/redux/exchange-orders/exchange-orders-models';
import Presentation from '../../logic/redux/presentation';
import { Paths } from '../../navigation/paths';
import { FormattedMessage, useIntl } from 'react-intl';

const CloseConfirmModalContent = (props: {
  exchangeOrder: ExchangeOrder;
  onCancellationSuccess: () => void;
}): JSX.Element => {
  const dispatch = useDispatch();
  const { addToast } = useToasts();
  const { phoneNumber } = useUserPhoneNumber();
  const intl = useIntl();
  const [smsCode, setSMSCode] = useState<string>('');
  const [smsCodeError, setSMSCodeError] = useState<string>('');
  const [token, setToken] = useState<string | undefined>(undefined);

  const getUserNeedTransactionSecondConfirmationQueryResult = useQuery<Query>({
    query: GraphqlOperations.queries.getUserNeedTransactionSecondConfirmation.queryString,
  })[0];
  const needsConfirmation =
    getUserNeedTransactionSecondConfirmationQueryResult.data?.getUser
      ?.need_transaction_second_confirmation;

  const [cancelResult, cancelExchangeOrder] = useMutation(
    GraphqlOperations.mutations.cancel_exchange_order.mutationString,
  );

  const [cancelConfirmResult, cancelExchangeOrderConfirm] = useMutation(
    GraphqlOperations.mutations.cancel_exchange_order_confirm.mutationString,
  );

  const handleConfirmedCancellation = (): void => {
    if (!token || !cancelResult.data) {
      return;
    }
    const variables = GraphqlOperations.mutations.cancel_exchange_order_confirm.createVars(
      smsCode,
      token,
    );
    cancelExchangeOrderConfirm(variables)
      .then((result) => {
        if (result && !result.error) {
          addToast(
            intl.formatMessage({ id: 'exchangeOrderDetails.cancelConfirmation.toast.success' }),
            {
              appearance: 'success',
              autoDismiss: true,
            },
          );
          props.onCancellationSuccess();
          dispatch(Presentation.actions.setShowModal(undefined));
        } else {
          if (needsConfirmation) {
            setSMSCodeError(
              intl.formatMessage({
                id: 'exchangeOrderDetails.cancelConfirmation.errors.invalidPassword',
              }),
            );
          }
        }
        return true;
      })
      .catch(() =>
        addToast(
          intl.formatMessage({ id: 'exchangeOrderDetails.cancelConfirmation.toast.error' }),
          {
            appearance: 'error',
            autoDismiss: true,
          },
        ),
      );
  };

  useEffect(() => {
    const variables = GraphqlOperations.mutations.cancel_exchange_order.createVars(
      props.exchangeOrder.id,
    );
    cancelExchangeOrder(variables)
      .then((result) => {
        if (result && !result.error) {
          setToken(result.data.cancel_exchange_order);
        } else {
          addToast(
            intl.formatMessage({ id: 'exchangeOrderDetails.cancelConfirmation.toast.error' }),
            {
              appearance: 'error',
              autoDismiss: true,
            },
          );
        }
        return true;
      })
      .catch(() =>
        addToast(
          intl.formatMessage({ id: 'exchangeOrderDetails.cancelConfirmation.toast.error' }),
          {
            appearance: 'error',
            autoDismiss: true,
          },
        ),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSMSChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setSMSCode(event.currentTarget.value);
  };

  const handleCancelClick = (): void => {
    dispatch(Presentation.actions.setShowModal(undefined));
  };

  const confirmationDependentContent = needsConfirmation ? (
    <>
      <p
        className="text-14 mb-6"
        dangerouslySetInnerHTML={{
          __html: intl.formatMessage(
            {
              id: 'exchangeOrderDetails.cancelConfirmation.description',
            },
            { phoneNumber },
          ),
        }}
      />
      <StyledInput
        type="password"
        placeholder={intl.formatMessage({
          id: 'exchangeOrderDetails.cancelConfirmation.smsCodePlaceholder',
        })}
        onChange={handleSMSChange}
        error={smsCodeError}
        onEnterPress={handleConfirmedCancellation}
      />
    </>
  ) : (
    <p className="text-12 mb-16">
      <FormattedMessage id="exchangeOrderDetails.cancelConfirmation.prompt" />
    </p>
  );

  return (
    <div className="flex flex-col w-1/1">
      <h2 className="mb-4">
        <FormattedMessage id="exchangeOrderDetails.cancelConfirmation.title" />
      </h2>
      {getUserNeedTransactionSecondConfirmationQueryResult.fetching ? (
        <CenteredSpinner />
      ) : (
        confirmationDependentContent
      )}
      <div className="flex flex-row justify-end mt-6">
        <Button className="mr-6" onClick={handleCancelClick} secondary>
          <FormattedMessage id="exchangeOrderDetails.cancelConfirmation.cancelButton" />
        </Button>
        <Button
          onClick={handleConfirmedCancellation}
          inactive={!cancelResult.data}
          loading={cancelConfirmResult.fetching}
        >
          <FormattedMessage id="exchangeOrderDetails.cancelConfirmation.confirmButton" />
        </Button>
      </div>
    </div>
  );
};

const ExchangeOrderDetails = (): JSX.Element => {
  const history = useHistory();
  const match = useRouteMatch();
  const dispatch = useDispatch();
  const intl = useIntl();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const id: string | undefined = (match.params as any).id;
  const idNum = id ? Number.parseInt(id) : NaN;

  const [exchangeOrderQueryResult, reexecuteExchangeOrderQuery] = useQuery<Query>({
    query: GraphqlOperations.queries.get_exchange_order.queryString,
    variables: GraphqlOperations.queries.get_exchange_order.createVars(
      !isNaN(idNum) ? idNum.valueOf() : 0,
    ),
    pause: isNaN(idNum),
    requestPolicy: 'network-only',
  });
  const exchangeOrder = exchangeOrderQueryResult.data?.get_exchange_order;

  // Needed to set account details for edited rejected exchange order
  const userAccountsQueryResult = useQuery<Query>({
    query: GraphqlOperations.queries.getUserAccounts.queryString,
    // requestPolicy: 'network-only',
    pause: !exchangeOrder || exchangeOrder.status !== Status.Rejected,
  })[0];

  const handleUpdateExchangeOrderSubscription = (
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    subscriptionExchangeOrders: ExchangeOrderListItem[] = [],
    response: Subscription,
  ): ExchangeOrderListItem[] => {
    if (response && response.update_exchange_order) {
      if (
        response.update_exchange_order.id === exchangeOrderQueryResult.data?.get_exchange_order.id
      ) {
        reexecuteExchangeOrderQuery();
      }
    }
    return [];
  };

  useSubscription(
    {
      query: GraphqlOperations.subscriptions.update_exchange_order.subscriptionString,
    },
    handleUpdateExchangeOrderSubscription,
  );

  const handleBackClick = (): void => {
    history.goBack();
  };

  const handleCancellationSuccess = (): void => {
    reexecuteExchangeOrderQuery();
  };

  const handleCancelClick = (): void => {
    if (!exchangeOrder) return;
    dispatch(
      Presentation.actions.setShowModal(
        <CloseConfirmModalContent
          exchangeOrder={exchangeOrder}
          onCancellationSuccess={handleCancellationSuccess}
        />,
      ),
    );
  };

  const handleEditClick = (): void => {
    const accountDetails = userAccountsQueryResult.data?.getUser?.accounts?.find(
      (a) => a.number === exchangeOrder?.account,
    );
    if (!exchangeOrder || !accountDetails) return;
    const editedExchangeOrder: EditedCreateExchangeOrder = {
      edited_id: exchangeOrder.id,
      direction: exchangeOrder.direction,
      account: exchangeOrder.account,
      security: exchangeOrder.security.short_name,
      count: exchangeOrder.count,
      daytrade: !!exchangeOrder.daytrade,
      daytrade_type: exchangeOrder.daytrade_type || undefined,
      type: exchangeOrder.type,
      limit_price: exchangeOrder.limit_price || undefined,
      activation_price: exchangeOrder.activation_price || undefined,
      outside_trading_hours: !!exchangeOrder.outside_trading_hours,
      validity_start: exchangeOrder.validity_start,
      validity_end: exchangeOrder.validity_end,
      accountDetails,
      securityDetails: exchangeOrder.security,
    };
    dispatch(ExchangeOrders.actions.setEditedExchangeOrder(editedExchangeOrder));
    history.push(Paths.new_exchange_order);
  };

  if (isNaN(idNum)) {
    return <Redirect to={Paths.exchange_orders} />;
  }

  const title = exchangeOrder?.security.name
    ? exchangeOrder?.security.name +
      intl.formatMessage({ id: 'exchangeOrderDetails.titleExtension' })
    : intl.formatMessage({ id: 'exchangeOrderDetails.defaultTitle' });
  const titleWithTransactionCode = exchangeOrder?.transaction_code
    ? title + ' - ' + exchangeOrder.transaction_code
    : title;

  const isShowingCancelButton =
    (exchangeOrder?.status === Status.Active || exchangeOrder?.status === Status.Partial) &&
    (exchangeOrder?.cancel_accepted === null || exchangeOrder?.cancel_accepted === undefined);

  const isShowingCancelRejectedContent =
    (exchangeOrder?.status === Status.Active || exchangeOrder?.status === Status.Partial) &&
    exchangeOrder?.cancel_accepted === false;

  return (
    <DefaultScreen
      background={assets.images.exchange_order_bg}
      title={titleWithTransactionCode}
      onBackClick={handleBackClick}
      loading={exchangeOrderQueryResult.fetching}
      rightContent={
        <aside className="flex flex-row items-center">
          <StatusIcon status={exchangeOrder?.status} />
          <h2 className="ml-2">{stringifyStatus(exchangeOrder?.status, intl)}</h2>
        </aside>
      }
    >
      {isShowingCancelRejectedContent ? (
        <ErrorShower
          error={intl.formatMessage(
            {
              id: 'exchangeOrderDetails.errors.cancelRejected',
            },
            {
              reason:
                intl.locale === 'en' && exchangeOrder?.cancel_reject_reason_en !== ''
                  ? exchangeOrder?.cancel_reject_reason_en
                  : exchangeOrder?.cancel_reject_reason,
            },
          )}
          className="mb-3"
          bigger
        />
      ) : null}
      {exchangeOrder?.status === Status.Rejected ? (
        <ErrorShower
          error={
            intl.locale === 'en' && exchangeOrder?.reject_reason_en !== ''
              ? exchangeOrder?.reject_reason_en
              : exchangeOrder.reject_reason
          }
          className="mb-3"
          bigger
        />
      ) : null}
      <div className="grid row-gap-1 col-gap-3  grid-cols-1 lg:grid-cols-2 mb-6">
        <div className="w-1/1">
          <BlurredContainerM2030 className="mb-1">
            <h2 className="mb-6">
              <FormattedMessage id="exchangeOrderDetails.overview" />
            </h2>
            <DataRow
              label={intl.formatMessage({ id: 'exchangeOrderDetails.securityNameLabel' })}
              value={
                (exchangeOrder?.security.name || '') +
                intl.formatMessage({ id: 'exchangeOrderDetails.securityNameExtension' })
              }
            />
            <DataRow
              label={intl.formatMessage({ id: 'exchangeOrderDetails.directionLabel' })}
              value={intl.formatMessage({ id: `order.direction.${exchangeOrder?.direction}` })}
            />
            <DataRow
              label={intl.formatMessage({ id: 'exchangeOrderDetails.accountLabel' })}
              value={exchangeOrder?.account}
            />
            {exchangeOrder?.daytrade ? (
              <div>
                <DataRow
                  label={intl.formatMessage({ id: 'exchangeOrderDetails.dayTradeLabel' })}
                  value={intl.formatMessage({
                    id: exchangeOrder?.daytrade ? 'common.yes' : 'common.no',
                  })}
                />
                <DataRow
                  label={intl.formatMessage({ id: 'exchangeOrderDetails.dayTradeTypeLabel' })}
                  value={intl.formatMessage({
                    id: `order.dayTradeType.${exchangeOrder?.daytrade_type}`,
                  })}
                />
              </div>
            ) : null}
            <DataRow
              label={intl.formatMessage({ id: 'exchangeOrderDetails.exchangeTypeLabel' })}
              value={stringifyExchangeRateType(exchangeOrder?.type, intl)}
            />
          </BlurredContainerM2030>
          <BlurredContainerM2030>
            <h2 className="mb-6">
              <FormattedMessage id="exchangeOrderDetails.fulfilledLabel" />
            </h2>
            <DataRow
              label={intl.formatMessage({ id: 'exchangeOrderDetails.fulfilledCountLabel' })}
              value={formatNumber(exchangeOrder?.fulfilled_count, 'db')}
            />
            <DataRow
              label={intl.formatMessage({ id: 'exchangeOrderDetails.dealPriceLabel' })}
              value={formatNumber(
                exchangeOrder?.deal_price,
                `${exchangeOrder?.security.currency.currency} / ${intl.formatMessage({
                  id: 'exchangeOrderDetails.pcs',
                })}`,
                6,
              )}
            />
          </BlurredContainerM2030>
        </div>
        <BlurredContainerM2030>
          <h2 className="mb-6">
            <FormattedMessage id={'exchangeOrderDetails.securityLabel'} />
          </h2>
          <DataRow
            label={intl.formatMessage({ id: 'exchangeOrderDetails.localityLabel' })}
            value={exchangeOrder?.security.stock_market_locality}
          />
          <DataRow
            label={intl.formatMessage({ id: 'exchangeOrderDetails.outsideTradingHoursLabel' })}
            value={intl.formatMessage({
              id: exchangeOrder?.outside_trading_hours ? 'common.yes' : 'common.no',
            })}
          />
          <DataRow
            label={intl.formatMessage({ id: 'exchangeOrderDetails.countLabel' })}
            value={formatNumber(exchangeOrder?.count, 'db')}
          />
          <DataRow
            label={intl.formatMessage({ id: 'exchangeOrderDetails.limitPriceLabel' })}
            value={formatNumber(
              exchangeOrder?.limit_price,
              exchangeOrder?.security.currency.currency,
            )}
          />
          <DataRow
            label={intl.formatMessage({ id: 'exchangeOrderDetails.activationPriceLabel' })}
            value={formatNumber(
              exchangeOrder?.activation_price,
              exchangeOrder?.security.currency.currency,
            )}
          />
          <DataRow
            label={intl.formatMessage({ id: 'exchangeOrderDetails.validityLabel' })}
            value={
              exchangeOrder?.validity_end
                ? `${formatDate(exchangeOrder.validity_start)} - ${formatDate(
                    exchangeOrder.validity_end,
                  )}`
                : intl.formatMessage({ id: 'exchangeOrderDetails.toCancellation' })
            }
          />
        </BlurredContainerM2030>
      </div>
      {isShowingCancelButton ? (
        <Button secondary onClick={handleCancelClick}>
          <FormattedMessage id="exchangeOrderDetails.cancelOrderButton" />
        </Button>
      ) : null}
      {exchangeOrder?.status === Status.Rejected ? (
        <Button
          onClick={handleEditClick}
          inactive={!userAccountsQueryResult.data?.getUser?.accounts}
        >
          <FormattedMessage id="exchangeOrderDetails.editButton" />
        </Button>
      ) : null}
    </DefaultScreen>
  );
};

export default withErrorBoundary(ExchangeOrderDetails, { fallback: Fallback });
